import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';

// ============================================================================
// Types
// ============================================================================

// Generic array reset function type - accepts any setter that can be called with empty array
type ArrayResetter = (value: never[]) => void;

export interface UseEventChangeCleanupParams {
  selectedEvent: EventWithCircuit | null;
  // Data state setters
  setSelectedDateRange: React.Dispatch<React.SetStateAction<{ start: Date; end: Date } | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setRadarActivity: ArrayResetter;
  setStationActivity: ArrayResetter;
  setDailyForecasts: ArrayResetter;
  setHourlyForecasts: ArrayResetter;
  setRainForecasts: ArrayResetter;
  setMinutelyForecasts: ArrayResetter;
  setInfos: ArrayResetter;
  setCurrentInfoIndex: React.Dispatch<React.SetStateAction<number>>;
  setStations: ArrayResetter;
  setSelectedStationIndex: React.Dispatch<React.SetStateAction<number>>;
  setSelectedTimestamp: (timestamp: Date | null) => void;
  // Playback refs
  hasManuallyPausedRef: React.RefObject<boolean>;
  hasManuallyPausedTioRef: React.RefObject<boolean>;
  // State reset function
  resetState: (options?: { observationsEnabled?: boolean; selectedEvent?: EventWithCircuit | null }) => void;
  // Map ref and layer IDs
  mapRef: React.RefObject<MapboxMap | null>;
  radarLayerId: string;
  radarSourceId: string;
  radarLayerId2: string;
  radarSourceId2: string;
  tioLayerId: string;
  tioSourceId: string;
  tioLayerId2: string;
  tioSourceId2: string;
  // Event tracking ref
  previousEventRef: React.RefObject<string | null>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to handle cleanup when the selected event changes.
 * 
 * Responsibilities:
 * - Clear data fetching state when event changes
 * - Reset playback refs
 * - Reset centralized state (tiles, playback, Tomorrow.io state)
 * - Remove radar and TIO layers from the map
 * - Track previous event to detect changes
 */
export function useEventChangeCleanup({
  selectedEvent,
  setSelectedDateRange,
  setError,
  setRadarActivity,
  setStationActivity,
  setDailyForecasts,
  setHourlyForecasts,
  setRainForecasts,
  setMinutelyForecasts,
  setInfos,
  setCurrentInfoIndex,
  setStations,
  setSelectedStationIndex,
  setSelectedTimestamp,
  hasManuallyPausedRef,
  hasManuallyPausedTioRef,
  resetState,
  mapRef,
  radarLayerId,
  radarSourceId,
  radarLayerId2,
  radarSourceId2,
  tioLayerId,
  tioSourceId,
  tioLayerId2,
  tioSourceId2,
  previousEventRef,
}: UseEventChangeCleanupParams): void {
  useEffect(() => {
    // Create a unique key for the current event
    const currentEventKey = selectedEvent
      ? `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`
      : null;

    // Check if the event has changed (not just on initial load)
    const eventChanged = previousEventRef.current !== null && previousEventRef.current !== currentEventKey;

    if (!selectedEvent || eventChanged) {
      // Clear data fetching state
      setSelectedDateRange(null);
      setError(null);
      setRadarActivity([]);
      setStationActivity([]);
      setDailyForecasts([]);
      setHourlyForecasts([]);
      setRainForecasts([]);
      setMinutelyForecasts([]);
      setInfos([]);
      setCurrentInfoIndex(0);
      setStations([]);
      setSelectedStationIndex(0);
      setSelectedTimestamp(null);
      
      // Reset playback refs
      hasManuallyPausedRef.current = false;
      hasManuallyPausedTioRef.current = false;
      
      // Reset centralized state (tiles, playback, Tomorrow.io state, etc.)
      const newObservationsEnabled = selectedEvent?.observations_enabled !== false;
      resetState({
        observationsEnabled: newObservationsEnabled,
        selectedEvent,
      });

      // Remove radar and tio layers from map if they exist
      const map = mapRef.current;
      if (map) {
        removeMapLayers(map, {
          radarLayerId,
          radarSourceId,
          radarLayerId2,
          radarSourceId2,
          tioLayerId,
          tioSourceId,
          tioLayerId2,
          tioSourceId2,
        });
      }
    }

    // Update the previous event ref
    previousEventRef.current = currentEventKey;
  }, [
    selectedEvent,
    setSelectedTimestamp,
    radarLayerId,
    radarSourceId,
    radarLayerId2,
    radarSourceId2,
    tioLayerId,
    tioSourceId,
    tioLayerId2,
    tioSourceId2,
    hasManuallyPausedRef,
    hasManuallyPausedTioRef,
    setCurrentInfoIndex,
    setDailyForecasts,
    setError,
    setHourlyForecasts,
    setInfos,
    setRadarActivity,
    setRainForecasts,
    setMinutelyForecasts,
    setSelectedDateRange,
    setSelectedStationIndex,
    setStationActivity,
    setStations,
    resetState,
    mapRef,
    previousEventRef,
  ]);
}

// ============================================================================
// Helper Functions
// ============================================================================

interface LayerIds {
  radarLayerId: string;
  radarSourceId: string;
  radarLayerId2: string;
  radarSourceId2: string;
  tioLayerId: string;
  tioSourceId: string;
  tioLayerId2: string;
  tioSourceId2: string;
}

/**
 * Remove all radar and TIO layers from the map
 */
function removeMapLayers(map: MapboxMap, ids: LayerIds): void {
  try {
    // Remove both radar layers (double-buffered)
    if (map.getLayer(ids.radarLayerId)) map.removeLayer(ids.radarLayerId);
    if (map.getLayer(ids.radarLayerId2)) map.removeLayer(ids.radarLayerId2);
    if (map.getSource(ids.radarSourceId)) map.removeSource(ids.radarSourceId);
    if (map.getSource(ids.radarSourceId2)) map.removeSource(ids.radarSourceId2);
    // Remove tio layers
    if (map.getLayer(ids.tioLayerId)) map.removeLayer(ids.tioLayerId);
    if (map.getLayer(ids.tioLayerId2)) map.removeLayer(ids.tioLayerId2);
    if (map.getSource(ids.tioSourceId)) map.removeSource(ids.tioSourceId);
    if (map.getSource(ids.tioSourceId2)) map.removeSource(ids.tioSourceId2);
  } catch {
    // Ignore errors if map is not ready
  }
}
